import { useMemo } from "react";
import { Box, Card, CardContent, Typography } from "@mui/material";
import temporalDataJson from "@/_data/dashboard/temporal_data_092826.json";
import type { UserMetricCard } from "./tabs/UserStatisticsTab";

import { Paid, IncompleteCircle, Public } from "@mui/icons-material";

type TemporalDataRow = {
  DocumentYear?: number | string | null;
  Country?: string | null;
  creditorcountry?: string | null;
  TotalCommitmentAmount?: number | null;
  BorrowerDebttoGDPCategory?: string | null;
};

type MetricCard = {
  label: string;
  value: string;
  delta: string;
  deltaColor: string;
  valueColor?: string;
  icon: React.ReactNode;
};

type AnalyticsMetricCardsProps = {
  activeTab: number;
  userMetrics: UserMetricCard[];
};

// Set midpoint value for debt to gpd range
const DEBT_TO_GDP_MIDPOINTS: Record<string, number> = {
  "<25%": 12.5,
  "25-50%": 37.5,
  "50-75%": 62.5,
  "75-100%": 87.5,
  ">100%": 112.5,
};

// Helper functions to format card display values and deltas
const formatCurrencyCompact = (value: number): string => {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000_000_000) {
    return `$${(value / 1_000_000_000_000).toFixed(1)}T`;
  }

  if (absValue >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  }

  if (absValue >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatSignedPercentage = (value: number): string => {
  const rounded = value.toFixed(1).replace(/\.0$/, "");
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${rounded}% (2024 to 2025)`;
};

const formatSignedCount = (value: number): string => {
  const prefix = value > 0 ? "+" : "";
  return `${prefix}${value} (2024 to 2025)`;
};

// Format value to display in increments of 5 with a +
const formatRoundedCountWithPlus = (value: number): string => {
  const safeValue = Number.isFinite(value) ? Math.max(0, Math.round(value) - 1) : 0;
  const roundedDownToFive = Math.floor(safeValue / 5) * 5;
  return `${roundedDownToFive}+`;
};

// Compute percentage change
const calculatePercentageChange = (
  currentValue: number | null,
  previousValue: number | null,
): number => {
  if (
    currentValue === null ||
    previousValue === null ||
    !Number.isFinite(currentValue) ||
    !Number.isFinite(previousValue) ||
    previousValue === 0
  ) {
    return 0;
  }

  return ((currentValue - previousValue) / previousValue) * 100;
};

// Helper function to compute metric values
const getRowsForYear = (rows: TemporalDataRow[], year: number) => {
  return rows.filter((row) => Math.round(Number(row.DocumentYear)) === year);
};

const sumCommitmentAmount = (rows: TemporalDataRow[]) => {
  return rows.reduce(
    (total, row) => total + Number(row.TotalCommitmentAmount ?? 0),
    0,
  );
};

const averageDebtToGdp = (rows: TemporalDataRow[]): number | null => {
  const values = rows
    .map((row) => DEBT_TO_GDP_MIDPOINTS[String(row.BorrowerDebttoGDPCategory)])
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return null;
  }

  return Math.round(
    values.reduce((sum, value) => sum + value, 0) / values.length,
  );
};

const countUniqueCountries = (rows: TemporalDataRow[]) => {
  return new Set(
    rows.map((row) => String(row.Country ?? "").trim()).filter(Boolean),
  ).size;
};

const countUniqueCreditorCountries = (rows: TemporalDataRow[]) => {
  return new Set(
    rows.map((row) => String(row.creditorcountry ?? "").trim()).filter(Boolean),
  ).size;
};

export default function AnalyticsMetricCards({
  activeTab,
  userMetrics,
}: AnalyticsMetricCardsProps) {
  const temporalRows = temporalDataJson.data as TemporalDataRow[];

  const metrics = useMemo<MetricCard[]>(() => {
    const colorTrendPositive = "#15803d";
    const colorTrendNegative = "#dc2626";

    const rows2024 = getRowsForYear(temporalRows, 2024);
    const rows2025 = getRowsForYear(temporalRows, 2025);

    const totalCommitmentAmount = sumCommitmentAmount(temporalRows);
    const commitmentDelta = calculatePercentageChange(
      sumCommitmentAmount(rows2025),
      sumCommitmentAmount(rows2024),
    );

    const debtToGdpDelta = calculatePercentageChange(
      averageDebtToGdp(rows2025),
      averageDebtToGdp(rows2024),
    );

    const borrowingCountries = countUniqueCountries(temporalRows);
    const borrowingCountriesDelta =
      countUniqueCountries(rows2025) - countUniqueCountries(rows2024);

    const creditorCountries = countUniqueCreditorCountries(temporalRows);
    const creditorCountriesDelta =
      countUniqueCreditorCountries(rows2025) -
      countUniqueCreditorCountries(rows2024);

    return [
      {
        label: "Captured Debt Commitments",
        value: formatCurrencyCompact(totalCommitmentAmount),
        delta: formatSignedPercentage(commitmentDelta),
        deltaColor: colorTrendPositive,
        icon: <Paid sx={{ color: "#5a5d62", fontSize: 22 }} />,
        valueColor: "#385bc7",
      },
      {
        label: "Debt to GDP Range",
        // value: formatPercentage(avgDebtToGdp),
        value: "19% - 139%",
        delta: formatSignedPercentage(debtToGdpDelta),
        deltaColor: colorTrendNegative,
        icon: <IncompleteCircle sx={{ color: "#5a5d62", fontSize: 22 }} />,
        valueColor: "#5a0066",
      },
      {
        label: "Borrowing Countries",
        value: formatRoundedCountWithPlus(borrowingCountries),
        delta: formatSignedCount(borrowingCountriesDelta),
        deltaColor: colorTrendNegative,
        icon: <Public sx={{ color: "#5a5d62", fontSize: 22 }} />,
        valueColor: "#2e5c18",
      },
      {
        label: "Creditors",
        value: formatRoundedCountWithPlus(creditorCountries),
        delta: formatSignedCount(creditorCountriesDelta),
        deltaColor: colorTrendNegative,
        icon: <Public sx={{ color: "#5a5d62", fontSize: 22 }} />,
        valueColor: "#017785",
      },
    ];
  }, [temporalRows]);

  const cards =
    activeTab === 3
      ? userMetrics.map((metric) => ({
          label: metric.label,
          value: String(metric.value),
          delta: "",
          deltaColor: "transparent",
          valueColor: metric.color,
          icon: metric.icon,
        }))
      : metrics;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr 1fr",
          lg: "repeat(4, 1fr)",
        },
        gap: "1rem",
      }}
    >
      {cards.map((metric) => (
        <Card key={metric.label} sx={{ borderRadius: 2 }}>
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "1rem",
              py: "1rem !important",
            }}
          >
            <div>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: "#5f6773",
                }}
              >
                {metric.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  lineHeight: 1.1,
                  fontWeight: 800,
                  color: metric.valueColor ?? "#424242",
                  mt: 0.7,
                }}
              >
                {metric.value}
              </Typography>
              {/* {metric.delta ? (
                <Typography
                  sx={{
                    mt: 0.7,
                    fontSize: 14,
                    color: metric.deltaColor,
                    fontWeight: 600,
                  }}
                >
                  {metric.delta}
                </Typography>
              ) : null} */}
            </div>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "#e6e6e6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                padding: "4px",
              }}
            >
              {metric.icon}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
